import { createContext, useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import global from "../config/Global.json";
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();
export default AuthContext;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // Utility function to get safely parsed token from localStorage
  const getStoredToken = () => {
    try {
      const stored = localStorage.getItem("authToken");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Error parsing authToken from localStorage", err);
      return null;
    }
  };

  // Utility function to safely decode JWT
  const decodeToken = (token) => {
    try {
      return jwtDecode(token);
    } catch (err) {
      console.error("Invalid token decoding error:", err);
      return {};
    }
  };

  const getAPI = (API_NAME) => global.api.host + global.api[API_NAME];

  const [authToken, setAuthToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(() =>
    getStoredToken() ? decodeToken(getStoredToken().access) : {}
  );
  const [loading, setLoading] = useState(true);

  const loginUser = (e) => {
    e.preventDefault();

    axios
      .post(getAPI("token"), {
        username: e.target.username.value,
        password: e.target.password.value,
      })
      .then((response) => {
        const tokenData = response.data;

        if (global.checkSuperUser === true) {
          checkSuperUser(tokenData.access);
        }

        setAuthToken(tokenData);
        setUser(decodeToken(tokenData.access));
        localStorage.setItem("authToken", JSON.stringify(tokenData));

        toast.success("Login successful! Welcome back!");
      })
      .catch((error) => {
        const detail = error.response?.data?.detail;
        if (detail === "No active account found with the given credentials") {
          toast.error("Username or password incorrect. Please try again.");
        } else {
          toast.error("An error occurred. Please try again.");
        }
        console.error("Login error:", error);
      });
  };

  const checkSuperUser = async (token) => {
    try {
      const response = await axios.get(getAPI("isSuperUser"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.is_superuser) {
        toast.success("Welcome, Admin");
      } else {
        logOutUser("User is not super user");
      }
    } catch (error) {
      toast.error("Error checking user permissions");
      console.error(error);
    }
  };

  const logOutUser = (message) => {
    setAuthToken(null);
    setUser({});
    localStorage.removeItem("authToken");

    if (message) toast.error(message);

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const checkSuperUserRefresh = async (token) => {
      try {
        const response = await axios.get(getAPI("isSuperUser"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.data.is_superuser) {
          logOutUser("User is not super user");
        }
      } catch (error) {
        toast.error("Error checking user permissions");
        console.error(error);
      }
    };

    const refreshToken = () => {
      if (authToken?.refresh) {
        axios
          .post(getAPI("refreshToken"), { refresh: authToken.refresh })
          .then((response) => {
            if (global.checkSuperUser === true) {
              checkSuperUserRefresh(response.data.access);
            }

            setAuthToken(response.data);
            setUser(decodeToken(response.data.access));
            localStorage.setItem("authToken", JSON.stringify(response.data));
          })
          .catch(() => {
            logOutUser("Session expired. Please log in again.");
          });
      } else {
        logOutUser("No refresh token found");
      }

      if (loading) {
        setLoading(false);
      }
    };

    if (loading) {
      refreshToken();
    }

    const interval = setInterval(() => {
      if (authToken) {
        refreshToken();
      }
    }, 1000 * 60 * 4); // every 4 minutes

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [authToken, loading]);

  const contextData = {
    user,
    loginUser,
    logOutUser,
    authToken,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
      <Toaster />
    </AuthContext.Provider>
  );
};
